const BASE_URL = '/api/database/'
export const useDatabase = () => {
  const { fetchGetRes, fetchGet, fetchPost, fetchPostForm } = useCustomFetch()

  function createCollectingAndUpload(
    dbId: string,
    name: string,
    collectParam: CollectParams,
    convertParam: ConvertParams,
    file: File,
  ): Promise<Collecting> {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('name', name)
    formData.append('collectParam', JSON.stringify(collectParam))
    formData.append('convertParam', JSON.stringify(convertParam))
    return fetchPostForm(
      BASE_URL + dbId + '/collectings/create-and-upload',
      formData,
    )
  }

  function createCollectingAndCollect(
    dbId: string,
    name: string,
    collectParam: CollectParams,
    convertParam: ConvertParams,
  ): Promise<Collecting> {
    return fetchPostForm(BASE_URL + dbId + '/collectings/create-and-collect', {
      name,
      collectParam: JSON.stringify(collectParam),
      convertParam: JSON.stringify(convertParam),
    })
  }

  function getLabel(id: string): Promise<Label> {
    return fetchGet(BASE_URL + id + '/label')
  }

  function getLabelField(id: string): Promise<Label> {
    return fetchGet(BASE_URL + 'field/' + id)
  }

  function getDatabase(id: string): Promise<Database> {
    return fetchGet(BASE_URL + id)
  }

  function createDatabase(db: Database): Promise<Database> {
    return fetchPost(BASE_URL + 'create', db)
  }

  function updateDatabase(db: Database): Promise<Database> {
    return fetchPost(BASE_URL + 'update', db)
  }

  function searchDatabase(query: SearchQuery): Promise<SearchResult<Database>> {
    return fetchGet(BASE_URL + 'search' + toSearchQueryString(query))
  }

  //Collecting
  function getCollectings(dbId: string): Promise<Collecting[]> {
    return fetchGet(BASE_URL + dbId + '/collectings')
  }

  function getCollecting(
    dbId: string,
    collectingId: string,
    noCache?: boolean,
  ): Promise<Collecting> {
    return fetchGet(BASE_URL + dbId + '/collecting/' + collectingId)
  }

  function deleteCollecting(
    dbId: string,
    collectingId: string,
  ): Promise<Collecting> {
    return fetchPost(
      BASE_URL + dbId + '/collecting/' + collectingId + '/delete',
    )
  }

  function startAnalyze(dbId: string, collectingId: string): Promise<any> {
    return fetchPost(
      BASE_URL + dbId + '/collecting/' + collectingId + '/analyze',
    )
  }

  function getAnalyzeLabel(dbId: string, collectingId: string): Promise<Label> {
    return fetchGet(
      BASE_URL + dbId + '/collecting/' + collectingId + '/analyze-label',
    )
  }

  function getAnalyzeResult(
    dbId: string,
    collectingId: string,
  ): Promise<AnalyzeResult> {
    return fetchGet(
      BASE_URL + dbId + '/collecting/' + collectingId + '/analyze-result',
    )
  }

  function getLatestAnalyzeResult(dbId: string): Promise<AnalyzeResult> {
    return fetchGet(BASE_URL + dbId + '/latest-analyze-result')
  }

  function saveLabel(dbId: string, label: Label): Promise<Label> {
    return fetchPost(BASE_URL + dbId + '/label/update', label)
  }

  function startIndexing(
    dbId: string,
    collectingId: string,
    isTest: boolean = false,
    testRate: number = 1,
    clean: boolean = false,
  ): Promise<Collecting> {
    const formData: any = {
      isTest: String(isTest),
      clean: String(clean),
    }
    if (isTest) formData.testRate = String(testRate)
    return fetchPostForm(
      BASE_URL + dbId + '/collecting/' + collectingId + '/index',
      formData,
    )
  }

  function getCollectingDefinition(
    dbId: string,
  ): Promise<CollectingDefinition> {
    const url = BASE_URL + dbId + '/collecting-definition'
    return fetchGet(url)
  }

  function updateCollectingDefinition(
    dbId: string,
    cd: CollectingDefinition,
  ): Promise<CollectingDefinition> {
    return fetchPost(BASE_URL + dbId + '/collecting-definition/update', cd)
  }

  function collectImmediately(dbId: string): Promise<CollectingDefinition> {
    return fetchPost(BASE_URL + dbId + '/collecting-definition/collect')
  }

  function collectLogUrl(dbId: string, collectingId: string, logId: string) {
    return BASE_URL + `${dbId}/data/${collectingId}/log/${logId}`
  }

  function searchFieldLabel(
    query: SearchQuery,
  ): Promise<SearchResult<FieldLabel>> {
    return fetchGet(BASE_URL + 'search-field' + toSearchQueryString(query))
  }

  function getConvertScript(dbId: string): Promise<string> {
    return fetchGet(BASE_URL + dbId + '/script')
  }

  function postConvertScript(dbId: string, script: string): Promise<void> {
    const formData: any = {
      script: script,
    }
    return fetchPostForm(BASE_URL + dbId + '/script/update', formData)
  }

  function getIdMap(dbId: string) {
    return fetchGetRes(`${BASE_URL}${dbId}/idmap`, null, null, 'blob')
  }

  function updateIdMap(dbId: string, file: File): Promise<void> {
    const formData: any = {
      file: file,
    }    
    return fetchPostForm(BASE_URL + dbId + '/idmap/update', formData)
  }

  return {
    createCollectingAndUpload,
    createCollectingAndCollect,
    getLabel,
    getLabelField,
    getDatabase,
    createDatabase,
    updateDatabase,
    searchDatabase,
    getCollectings,
    getCollecting,
    deleteCollecting,
    startAnalyze,
    getAnalyzeLabel,
    getAnalyzeResult,
    getLatestAnalyzeResult,
    saveLabel,
    startIndexing,
    getCollectingDefinition,
    updateCollectingDefinition,
    collectImmediately,
    collectLogUrl,
    searchFieldLabel,
    getConvertScript,
    postConvertScript,
    getIdMap,
    updateIdMap,
  }
}
